/**
 * Wishlist Button — stylerotate.co.nz
 *
 * Reads window.__WISHLIST_CONFIG set by wishlist-config.liquid.
 * Handles both guest (localStorage) and logged-in (API) modes.
 * Supports multiple wishlists via a popover UI.
 *
 * Usage in product template:
 *   <button class="wishlist-btn"
 *     data-product-id="{{ product.id }}"
 *     data-variant-id="{{ product.selected_or_first_available_variant.id }}"
 *     data-product-handle="{{ product.handle }}"
 *     data-product-title="{{ product.title | escape }}"
 *     data-image-url="{{ product.featured_image | img_url: '400x' }}"
 *     data-price="{{ product.selected_or_first_available_variant.price | money_without_currency }}"
 *     data-currency="{{ cart.currency.iso_code }}">
 *     ♡ Save
 *   </button>
 */
(function () {
  'use strict';

  var GUEST_KEY = 'wishlist_guest';
  var SESSION_TOKEN_KEY = 'wishlist_token';
  var SESSION_CUSTOMER_KEY = 'wishlist_customer_id';

  var cfg = window.__WISHLIST_CONFIG;
  if (!cfg) return;

  // ─── Module-level state ─────────────────────────────────────────────────────

  var wlState = {
    wishlists: null,      // null = not loaded, [] = loaded but empty
    presenceMap: null,    // Map<variant_id, Set<wishlist_id>>
    modal: null,
  };

  // Legacy set — still used for guest mode and header count
  var wishlisted = new Set(); // variant IDs currently wishlisted
  var _activeToast = null;    // handle of the toast currently on screen

  // ─── Utility ────────────────────────────────────────────────────────────────

  function showToast(message, opts) {
    opts = opts || {};
    if (_activeToast) { _activeToast.cancel(); _activeToast = null; }

    var toast = document.createElement('div');
    toast.className = 'wl-toast';
    toast.setAttribute('role', 'status');
    toast.setAttribute('aria-live', 'polite');

    var msg = document.createElement('span');
    msg.className = 'wl-toast__msg';
    msg.textContent = message; // textContent — safe
    toast.appendChild(msg);

    var timer = null;
    function clearTimer() { if (timer) { clearTimeout(timer); timer = null; } }
    function dismiss() {
      clearTimer();
      if (_activeToast === handle) _activeToast = null;
      toast.classList.remove('wl-toast--show');
      toast.classList.add('wl-toast--hide');
      setTimeout(function () { if (toast.parentNode) toast.remove(); }, 220);
    }
    function cancel() {
      clearTimer();
      if (_activeToast === handle) _activeToast = null;
      if (toast.parentNode) toast.remove();
    }

    var hasAction = opts.action && opts.action.label && typeof opts.action.onClick === 'function';
    if (hasAction) {
      var actionBtn = document.createElement('button');
      actionBtn.className = 'wl-toast__action';
      actionBtn.type = 'button';
      actionBtn.textContent = opts.action.label; // textContent — safe
      actionBtn.addEventListener('click', function () {
        dismiss();
        opts.action.onClick();
      });
      toast.appendChild(actionBtn);
    }

    // Pause auto-dismiss on hover (all toasts)
    toast.addEventListener('mouseenter', clearTimer);
    toast.addEventListener('mouseleave', function () { if (!timer) timer = setTimeout(dismiss, 2500); });

    document.body.appendChild(toast);
    requestAnimationFrame(function () { toast.classList.add('wl-toast--show'); });
    timer = setTimeout(dismiss, hasAction ? 6000 : 3000);

    var handle = { dismiss: dismiss, cancel: cancel };
    _activeToast = handle;
    return handle;
  }

  // ─── Storage helpers ────────────────────────────────────────────────────────

  function getGuestItems() {
    try {
      return JSON.parse(localStorage.getItem(GUEST_KEY) || '[]');
    } catch (e) {
      return [];
    }
  }

  function saveGuestItems(items) {
    localStorage.setItem(GUEST_KEY, JSON.stringify(items));
  }

  // sessionStorage access itself can throw SecurityError where site data is
  // blocked (iOS "Block All Cookies", some webviews). Degrade to "guest" rather
  // than letting the exception escape into every caller.
  function getSessionToken() {
    try {
      return sessionStorage.getItem(SESSION_TOKEN_KEY);
    } catch (e) {
      return null;
    }
  }

  // Same guard as getSessionToken: sessionStorage access throws SecurityError
  // when site data is blocked (iOS "Block All Cookies", some webviews). Reading
  // it raw stops the caller dead — which is how the shared-list view used to
  // stick on "Loading shared list…" forever.
  function getStoredCustomerId() {
    try {
      return sessionStorage.getItem(SESSION_CUSTOMER_KEY);
    } catch (e) {
      return null;
    }
  }

  function setSessionToken(token, customerId) {
    sessionStorage.setItem(SESSION_TOKEN_KEY, token);
    sessionStorage.setItem(SESSION_CUSTOMER_KEY, customerId);
  }

  function formatShortDate(isoDate) {
    var p = isoDate.split('-');
    if (p.length !== 3) return isoDate;
    var months = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
    var d = parseInt(p[2], 10);
    var m = parseInt(p[1], 10) - 1;
    return (m >= 0 && m <= 11 && !isNaN(d)) ? d + ' ' + months[m] + ' ' + p[0] : isoDate;
  }

  // ─── API helpers ────────────────────────────────────────────────────────────

  function rawApiRequest(method, path, body) {
    var token = getSessionToken();
    var headers = { 'Content-Type': 'application/json' };
    if (token) headers['Authorization'] = 'Bearer ' + token;

    return fetch(cfg.apiBase + path, {
      method: method,
      headers: headers,
      body: body ? JSON.stringify(body) : undefined,
    }).then(function (r) {
      if (r.status === 204) return null;
      if (!r.ok) {
        return r.json().then(function (e) {
          if (e && typeof e === 'object') {
            if (e.status === undefined) e.status = r.status;
            // Backend error bodies are {error: {code, message}} — flatten so
            // isAuthError() can read err.code the same way callers already
            // read err.status, without disturbing any endpoint that already
            // returns a flat {code, message} body.
            if (e.code === undefined && e.error && e.error.code !== undefined) e.code = e.error.code;
          }
          throw e;
        }, function () {
          var err = new Error('HTTP ' + r.status);
          err.status = r.status;
          throw err;
        });
      }
      return r.json();
    });
  }

  // Retry-wrapped entry point: on a 401/auth-error from a re-loginable session,
  // silently mint a fresh token once and replay the request. Keeps expired
  // sessions from dead-ending every call for the rest of the tab's life.
  function apiRequest(method, path, body, _retried) {
    return rawApiRequest(method, path, body).catch(function (err) {
      if (!_retried && isAuthError(err) && canReauth()) {
        return reauth().then(function () {
          return apiRequest(method, path, body, true);
        });
      }
      throw err;
    });
  }

  // ─── Auth ───────────────────────────────────────────────────────────────────

  var pendingReauth = null; // single-flight guard — see reauth()

  function loginWithHmac(customer) {
    return rawApiRequest('POST', '/auth/login', {
      store_domain: cfg.storeDomain,
      customer_id: customer.id,
      hmac: cfg.hmac,
      hmac_ts: cfg.hmac_ts,
      first_name: customer.firstName || '',
    }).then(function (data) {
      if (data && data.token) {
        setSessionToken(data.token, customer.id);
        try { sessionStorage.removeItem('wishlist_hmac_reloaded'); } catch (e) {}
        return data.token;
      }
      throw new Error('Login failed');
    });
  }

  // Guests and HMAC-less configs must never attempt a re-login — there is
  // nothing to sign a fresh token request with.
  function canReauth() {
    return !!(cfg.customer && cfg.hmac);
  }

  // 403 STORE_MISMATCH is deliberately excluded — re-login cannot fix it and
  // would just loop.
  function isAuthError(err) {
    if (!err) return false;
    if (err.status === 401) return true;
    return err.code === 'MISSING_TOKEN' || err.code === 'TOKEN_EXPIRED' || err.code === 'TOKEN_INVALID';
  }

  // Was this page rendered recently enough that its HMAC *should* still be
  // valid? Used as a second guard on the HMAC_EXPIRED reload: if a just-rendered
  // timestamp is already rejected as expired, the fault is a server clock skew
  // or a mismatched secret, and reloading only produces another equally-fresh
  // HMAC — i.e. an infinite reload loop the sessionStorage flag cannot stop when
  // setItem is also throwing. 120s is comfortably longer than a page render plus
  // the login round-trip, and far shorter than any sane backend freshness
  // window, so a genuinely stale (e.g. CDN-cached) page still reloads once.
  var HMAC_FRESH_SECONDS = 120;

  function pageHmacIsFresh() {
    var ts = parseInt(cfg.hmac_ts, 10);
    if (!ts || isNaN(ts)) return false;
    var ageSeconds = Math.floor(Date.now() / 1000) - ts;
    return ageSeconds >= 0 && ageSeconds < HMAC_FRESH_SECONDS;
  }

  // Single-flight re-login: N concurrent 401s share one /auth/login instead of
  // stampeding the backend. Uses .then/.catch (not .finally — ES5 file).
  function reauth() {
    if (pendingReauth) return pendingReauth;
    if (!canReauth()) return Promise.reject(new Error('Wishlist: reauth() called without a re-loginable session'));

    try {
      sessionStorage.removeItem(SESSION_TOKEN_KEY);
      sessionStorage.removeItem(SESSION_CUSTOMER_KEY);
    } catch (e) {}

    pendingReauth = loginWithHmac(cfg.customer).then(function (token) {
      pendingReauth = null;
      return token;
    }, function (err) {
      pendingReauth = null;
      // The HMAC baked into this page's config is dead; only a fresh render
      // can produce a new one. Reload once (sessionStorage guard) rather than
      // retrying forever — an unguarded reload-on-failure loops infinitely
      // when the secret is genuinely mismatched instead of just stale.
      if (err && err.code === 'HMAC_EXPIRED') {
        var reloadedKey = 'wishlist_hmac_reloaded';
        var alreadyReloaded = false;
        try { alreadyReloaded = sessionStorage.getItem(reloadedKey) === '1'; } catch (e) {}
        if (!alreadyReloaded && !pageHmacIsFresh()) {
          try { sessionStorage.setItem(reloadedKey, '1'); } catch (e) {}
          location.reload();
        }
      }
      throw err;
    });
    return pendingReauth;
  }

  // ─── HTML escaping (for innerHTML) ───────────────────────────────────────────

  function escapeHtml(str) {
    return String(str)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }

  // ─── Shortlist sharing (owner) ───────────────────────────────────────────────

  // Enable sharing for a non-default shortlist → resolves with { token, share_path }.
  function shareShortlist(shortlistId) {
    return apiRequest('POST', '/wishlist/' + shortlistId + '/share');
  }

  // Revoke sharing for a shortlist.
  function unshareShortlist(shortlistId) {
    return apiRequest('DELETE', '/wishlist/' + shortlistId + '/share');
  }

  // Build the full, externally-shareable URL for a share token.
  function buildShareUrl(token) {
    if (!token) return null;
    return 'https://' + cfg.storeDomain + '/pages/wishlist?shared=' + encodeURIComponent(token);
  }

  // Copy text to the clipboard. Resolves true on success, false otherwise.
  function copyToClipboard(text) {
    if (navigator.clipboard && navigator.clipboard.writeText) {
      return navigator.clipboard.writeText(text).then(function () { return true; }, function () {
        return legacyCopy(text);
      });
    }
    return Promise.resolve(legacyCopy(text));
  }

  function legacyCopy(text) {
    try {
      var ta = document.createElement('textarea');
      ta.value = text;
      ta.setAttribute('readonly', '');
      ta.style.position = 'absolute';
      ta.style.left = '-9999px';
      document.body.appendChild(ta);
      ta.select();
      var ok = document.execCommand('copy');
      document.body.removeChild(ta);
      return ok;
    } catch (e) {
      return false;
    }
  }

  // ─── Shared shortlist view (recipient) ───────────────────────────────────────
  //
  // Runs entirely inside this IIFE so it can reuse loginWithHmac + apiRequest,
  // which are private here. Called from the wishlist page when ?shared={token}
  // is present. Renders a strictly read-only panel into `mountEl`.
  //
  function renderSharedView(token, mountEl) {
    if (!mountEl || !token) return;

    var i18n = (cfg && cfg.i18n) || {};

    // Logged-out → caller (page) shows the existing login teaser. We only run
    // the authenticated path; bail if there's no Shopify customer.
    if (!cfg.customer) return;

    mountEl.innerHTML = '<p class="wl-shared-loading">' +
      escapeHtml(i18n.sharedLoading || 'Loading shared list…') + '</p>';

    ensureRecipientSession()
      .then(function () { return apiRequest('GET', '/shared/' + encodeURIComponent(token)); })
      .then(function (data) { renderSharedPanel(token, data, mountEl); })
      .catch(function (err) {
        var status = err && (err.status || err.statusCode);
        var code = err && err.code;
        if (status === 404 || code === 'SHARE_NOT_FOUND') {
          renderSharedUnavailable(mountEl);
        } else {
          mountEl.innerHTML = '<p class="wl-shared-error">' +
            escapeHtml(i18n.sharedError || 'Could not load this shared list. Please try again later.') +
            '</p>';
        }
      });
  }

  // Ensure the viewer has a valid session token (their OWN identity).
  function ensureRecipientSession() {
    var storedCustomerId = getStoredCustomerId();
    var haveSession = getSessionToken() && storedCustomerId === String(cfg.customer.id);
    if (haveSession) return Promise.resolve();
    return reauth();
  }

  function renderSharedUnavailable(mountEl) {
    var i18n = (cfg && cfg.i18n) || {};
    mountEl.innerHTML =
      '<div class="wl-shared-panel wl-shared-panel--gone" role="region">' +
        '<h2 class="wl-section-title wl-shared-title">' +
          escapeHtml(i18n.sharedGoneTitle || 'This list is no longer available') + '</h2>' +
        '<p class="wl-shared-gone__text">' +
          escapeHtml(i18n.sharedGoneText || 'The owner may have stopped sharing it or removed it.') + '</p>' +
        '<a class="btn btn--primary wl-shared-gone__cta" href="/pages/wishlist">' +
          escapeHtml(i18n.sharedGoneCta || 'Go to my wishlist') + '</a>' +
      '</div>';
  }

  function renderSharedPanel(token, data, mountEl) {
    data = data || {};
    var i18n = (cfg && cfg.i18n) || {};
    var items = Array.isArray(data.items) ? data.items : [];
    var name = data.name || '';
    var owner = data.owner_first_name || '';

    var sharedByTpl = i18n.sharedBy || 'Shared by {name}';
    var sharedByText = sharedByTpl.replace('{name}', function() { return escapeHtml(owner); });

    var panel = document.createElement('div');
    panel.className = 'wl-shared-panel';
    panel.setAttribute('role', 'region');

    var header = document.createElement('div');
    header.className = 'wl-shared-header';
    var dateHtml = data.event_date
      ? '<span class="wl-shared-date">' + escapeHtml(formatShortDate(data.event_date)) + '</span>'
      : '';
    header.innerHTML =
      '<h1 class="wl-section-title wl-shared-title">' + escapeHtml(name) + '</h1>' +
      '<div class="wl-shared-meta">' +
        '<span class="wl-shared-by">' + sharedByText + '</span>' +
        dateHtml +
      '</div>';
    panel.appendChild(header);

    // Primary CTA — copy into the viewer's own wishlist.
    var cta = document.createElement('button');
    cta.type = 'button';
    cta.className = 'btn btn--primary wl-shared-copy-btn';
    cta.textContent = i18n.sharedCopyCta || 'Add these to my wishlist';
    if (!items.length) cta.disabled = true;
    cta.addEventListener('click', function () {
      copySharedToWishlist(token, cta);
    });
    panel.appendChild(cta);

    var grid = document.createElement('div');
    grid.className = 'wl-items-grid wl-shared-items-grid';
    if (!items.length) {
      grid.innerHTML = '<p class="wl-shortlist-empty">' +
        escapeHtml(i18n.sharedEmpty || 'This list has no items.') + '</p>';
    } else {
      items.forEach(function (item) {
        grid.appendChild(makeSharedCardWrapper(item));
      });
    }
    panel.appendChild(grid);

    mountEl.innerHTML = '';
    mountEl.appendChild(panel);
  }

  function copySharedToWishlist(token, btn) {
    var i18n = (cfg && cfg.i18n) || {};
    if (btn.dataset.wlBusy === '1') return;
    btn.dataset.wlBusy = '1';
    btn.disabled = true;
    apiRequest('POST', '/shared/' + encodeURIComponent(token) + '/copy')
      .then(function () {
        showToast(i18n.sharedCopied || 'Added to your wishlist', {
          action: {
            label: i18n.sharedCopiedCta || 'View',
            onClick: function () { window.location.href = '/pages/wishlist'; },
          },
        });
        btn.textContent = i18n.sharedCopiedDone || 'Added to your wishlist';
      })
      .catch(function (err) {
        btn.disabled = false;
        var status = err && (err.status || err.statusCode);
        var code = err && err.code;
        if (status === 404 || code === 'SHARE_NOT_FOUND') {
          showToast(i18n.sharedGoneToast || 'This list is no longer available');
        } else {
          showToast(i18n.sharedCopyError || 'Could not add to your wishlist — please try again');
        }
      })
      .then(function () { delete btn.dataset.wlBusy; }, function () { delete btn.dataset.wlBusy; });
  }

  // Read-only card: reuses Section Rendering, hides the native save button so
  // the viewer's own save-state never leaks into the shared view.
  function makeSharedCardWrapper(item) {
    item = item || {};
    var handle = item.product_handle || '';
    var wrap = document.createElement('div');
    wrap.className = 'wl-native-card wl-shared-card';
    fetch('/products/' + encodeURIComponent(handle) + '?section_id=wishlist-product-card')
      .then(function (r) { return r.ok ? r.text() : null; })
      .then(function (html) {
        if (!wrap.isConnected) return;
        if (html && html.trim()) {
          wrap.innerHTML = html;
        } else {
          wrap.appendChild(sharedFallbackCard(item));
        }
      })
      .catch(function () {
        if (wrap.isConnected) wrap.appendChild(sharedFallbackCard(item));
      });
    return wrap;
  }

  function sharedFallbackCard(item) {
    item = item || {};
    var handle = item.product_handle || '';
    var price = item.price ? (item.currency ? item.currency + ' ' : '') + item.price : '';
    var inner = document.createElement('div');
    inner.className = 'wl-item-card';
    var imgHtml = item.image_url
      ? '<a href="/products/' + escapeHtml(handle) + '"><img src="' + escapeHtml(item.image_url) + '" alt="' + escapeHtml(item.product_title || '') + '" loading="lazy"></a>'
      : '<div class="wl-item-no-img"></div>';
    inner.innerHTML = imgHtml +
      '<div class="wl-item-info">' +
        '<a href="/products/' + escapeHtml(handle) + '" class="wl-item-title">' + escapeHtml(item.product_title || '') + '</a>' +
        (price ? '<p class="wl-item-price">' + escapeHtml(price) + '</p>' : '') +
      '</div>';
    return inner;
  }

  // ─── Multiple-wishlist state ─────────────────────────────────────────────────

  function buildPresenceMap(wishlists) {
    var map = new Map();
    (wishlists || []).forEach(function (wl) {
      (wl.items || []).forEach(function (item) {
        if (!map.has(item.variant_id)) map.set(item.variant_id, new Set());
        map.get(item.variant_id).add(wl.id);
      });
    });
    return map;
  }

  function variantInAnyWishlist(variantGid) {
    if (!wlState.presenceMap) return wishlisted.has(variantGid);
    return wlState.presenceMap.has(variantGid);
  }

  function getEverythingList() {
    return (wlState.wishlists || []).find(function (w) { return w.is_default; }) || null;
  }

  function variantInEverythingList(variantGid) {
    var el = getEverythingList();
    if (!el) return false;
    return !!(wlState.presenceMap && wlState.presenceMap.has(variantGid) && wlState.presenceMap.get(variantGid).has(el.id));
  }

  function getEverythingItemId(variantGid) {
    var el = getEverythingList();
    if (!el || !el.items) return null;
    var found = el.items.find(function (i) { return i.variant_id === variantGid; });
    return found ? found.id : null;
  }

  function loadAllWishlists() {
    if (!getSessionToken()) return Promise.resolve(null);
    return apiRequest('GET', '/wishlist/all')
      .then(function (data) {
        wlState.wishlists = Array.isArray(data) ? data : [];
        wlState.presenceMap = buildPresenceMap(wlState.wishlists);
        // Sync legacy wishlisted set for header count / guest fallback
        wishlisted.clear();
        wlState.presenceMap.forEach(function (_, variantId) {
          wishlisted.add(variantId);
        });
        return wlState.wishlists;
      })
      .catch(function () {
        return null;
      });
  }

  // ─── Wishlist state (legacy + new) ──────────────────────────────────────────

  function loadWishlistedIds() {
    if (getSessionToken()) {
      return loadAllWishlists()
        .then(function (lists) {
          if (lists === null) {
            // Fallback to single-wishlist endpoint
            return apiRequest('GET', '/wishlist?store_domain=' + encodeURIComponent(cfg.storeDomain))
              .then(function (data) {
                wishlisted.clear();
                if (data && data.items) {
                  data.items.forEach(function (item) { wishlisted.add(item.variant_id); });
                }
              })
              .catch(function () { /* silently degrade */ });
          }
        })
        .then(function () {
          refreshAllButtons();
        })
        .catch(function () { /* silently degrade */ });
    } else {
      var items = getGuestItems();
      wishlisted.clear();
      items.forEach(function (item) { wishlisted.add(item.variant_id); });
      refreshAllButtons();
      return Promise.resolve();
    }
  }

  function refreshAllButtons() {
    document.querySelectorAll('.wishlist-btn').forEach(updateButton);
    updateHeaderCount();
  }

  function updateHeaderCount() {
    var badge = document.querySelector('.header-wishlist-link__count');
    if (!badge) return;
    badge.textContent = wishlisted.size > 0 ? String(wishlisted.size) : '';
  }

  function updateButton(btn) {
    var variantId = btn.dataset.variantId;
    if (!variantId) return;
    var variantGid = 'gid://shopify/ProductVariant/' + variantId;
    var active = variantInAnyWishlist(variantGid);
    btn.setAttribute('aria-pressed', String(active));
    btn.setAttribute('aria-label', active ? 'Remove from wishlist' : 'Add to wishlist');
    btn.querySelector('.wishlist-icon') && (btn.querySelector('.wishlist-icon').textContent = active ? '♥' : '♡');
    btn.classList.toggle('wishlist-btn--active', active);
  }

  // ─── Payload builder ────────────────────────────────────────────────────────

  function buildItemPayload(btn) {
    return {
      store_domain: cfg.storeDomain,
      product_id: 'gid://shopify/Product/' + btn.dataset.productId,
      variant_id: 'gid://shopify/ProductVariant/' + btn.dataset.variantId,
      product_handle: btn.dataset.productHandle || '',
      product_title: btn.dataset.productTitle || '',
      image_url: btn.dataset.imageUrl || undefined,
      price: btn.dataset.price || undefined,
      currency: btn.dataset.currency || 'NZD',
    };
  }

  // ─── Shortlist assignment modal ─────────────────────────────────────────────

  function closeShortlistModal() {
    if (wlState.modal) {
      try { wlState.modal.close(); } catch (e) { /* not open */ }
      if (wlState.modal.parentNode) wlState.modal.remove();
      wlState.modal = null;
    }
  }

  // config: { everythingItemId, shortlists: [wl], onChange: fn|undefined }
  function openShortlistModal(config) {
    config = config || {};
    var everythingItemId = config.everythingItemId;
    var shortlists = config.shortlists || [];
    var onChange = typeof config.onChange === 'function' ? config.onChange : function () {};

    closeShortlistModal();

    var dialog = document.createElement('dialog');
    dialog.className = 'wl-modal';
    dialog.setAttribute('aria-labelledby', 'wl-modal-title');
    dialog.innerHTML =
      '<form method="dialog" class="wl-modal__inner">' +
        '<div class="wl-modal__header">' +
          '<h2 id="wl-modal-title" class="wl-modal__title">Add to event list</h2>' +
          '<button type="submit" class="wl-modal__close" aria-label="Close">&times;</button>' +
        '</div>' +
        '<div class="wl-modal__body"></div>' +
        '<div class="wl-modal__new">' +
          '<button type="button" class="wl-new-wishlist-btn">+ New event list</button>' +
          '<div class="wl-new-wishlist-form" style="display:none">' +
            '<input type="text" class="wl-new-wishlist-input" placeholder="Shortlist name" maxlength="100">' +
            '<div class="wl-new-wishlist-row">' +
              '<div class="wl-date-control" data-has-date="false">' +
                '<span class="wl-date-control__pill">' +
                  '<span class="wl-date-control__text">+ Add date</span>' +
                  '<input type="date" class="wl-date-control__input wl-new-date-input" aria-label="Event date">' +
                '</span>' +
                '<button type="button" class="wl-date-control__clear wl-new-date-clear" aria-label="Clear event date">&times;</button>' +
              '</div>' +
              '<button type="button" class="wl-new-wishlist-confirm">Create</button>' +
            '</div>' +
          '</div>' +
        '</div>' +
        '<div class="wl-modal__footer"><button type="submit" class="wl-popover-done">Done</button></div>' +
      '</form>';

    var body = dialog.querySelector('.wl-modal__body');
    if (!shortlists.length) {
      var empty = document.createElement('p');
      empty.className = 'wl-popover-empty';
      empty.textContent = 'No event lists yet.';
      body.appendChild(empty);
    } else {
      shortlists.forEach(function (sl) {
        body.appendChild(buildShortlistRow(sl, everythingItemId, onChange));
      });
    }

    var newBtn = dialog.querySelector('.wl-new-wishlist-btn');
    var newForm = dialog.querySelector('.wl-new-wishlist-form');
    var newInput = dialog.querySelector('.wl-new-wishlist-input');
    var newConfirm = dialog.querySelector('.wl-new-wishlist-confirm');
    var newDateCtrl = dialog.querySelector('.wl-new-date-input');
    var newDatePill = newDateCtrl && newDateCtrl.closest('.wl-date-control');
    var newDateClear = dialog.querySelector('.wl-new-date-clear');

    newBtn.addEventListener('click', function () { newForm.style.display = ''; newInput.focus(); });

    function updateNewDatePill(v) {
      if (newDatePill) newDatePill.setAttribute('data-has-date', v ? 'true' : 'false');
      var textEl = newDatePill && newDatePill.querySelector('.wl-date-control__text');
      if (textEl) textEl.textContent = v ? formatShortDate(v) : '+ Add date';
    }

    if (newDateCtrl && newDatePill) {
      newDatePill.querySelector('.wl-date-control__pill').addEventListener('click', function () {
        try { if (newDateCtrl.showPicker) newDateCtrl.showPicker(); } catch (e) {}
      });
      newDateCtrl.addEventListener('change', function () { updateNewDatePill(newDateCtrl.value); });
    }
    if (newDateClear) {
      newDateClear.addEventListener('click', function () {
        if (newDateCtrl) newDateCtrl.value = '';
        updateNewDatePill('');
      });
    }

    function createAndLink() {
      var name = newInput.value.trim();
      if (!name) { newInput.focus(); return; }
      newConfirm.disabled = true;
      var payload = { name: name };
      if (newDateCtrl && newDateCtrl.value) payload.event_date = newDateCtrl.value;
      apiRequest('POST', '/wishlist', payload)
        .then(function (newSl) {
          newSl.items = [];
          shortlists.push(newSl);
          if (wlState.wishlists && wlState.wishlists.indexOf(newSl) === -1) {
            wlState.wishlists.push(newSl);
          }
          return apiRequest('POST', '/wishlist/' + newSl.id + '/items', { wishlist_item_id: String(everythingItemId) })
            .then(function () {
              newSl.items.push({ id: everythingItemId });
              var emptyEl = body.querySelector('.wl-popover-empty');
              if (emptyEl) emptyEl.remove();
              body.appendChild(buildShortlistRow(newSl, everythingItemId, onChange));
              newInput.value = '';
              if (newDateCtrl) newDateCtrl.value = '';
              updateNewDatePill('');
              newForm.style.display = 'none';
              newConfirm.disabled = false;
              onChange();
            });
        })
        .catch(function () {
          newConfirm.disabled = false;
          showToast('Could not create event list — please try again');
        });
    }
    newConfirm.addEventListener('click', createAndLink);
    newInput.addEventListener('keydown', function (e) { if (e.key === 'Enter') { e.preventDefault(); createAndLink(); } });

    dialog.addEventListener('close', function () {
      if (dialog.parentNode) dialog.remove();
      if (wlState.modal === dialog) wlState.modal = null;
    });

    document.body.appendChild(dialog);
    wlState.modal = dialog;
    dialog.showModal();
  }

  function buildShortlistRow(sl, everythingItemId, onChange) {
    var inList = (sl.items || []).some(function (i) { return String(i.id) === String(everythingItemId); });
    var label = document.createElement('label');
    label.className = 'wl-checkbox-row';
    var cb = document.createElement('input');
    cb.type = 'checkbox';
    cb.checked = inList;
    cb.dataset.shortlistId = String(sl.id);
    var span = document.createElement('span');
    span.textContent = sl.name; // textContent — safe
    label.appendChild(cb);
    label.appendChild(span);
    cb.addEventListener('change', function () {
      toggleShortlistMembership(sl, everythingItemId, cb, onChange);
    });
    return label;
  }

  function toggleShortlistMembership(sl, everythingItemId, cb, onChange) {
    var add = cb.checked;
    cb.disabled = true;
    var req = add
      ? apiRequest('POST', '/wishlist/' + sl.id + '/items', { wishlist_item_id: String(everythingItemId) })
      : apiRequest('DELETE', '/wishlist/' + sl.id + '/items/' + everythingItemId);
    req.then(function () {
        if (!sl.items) sl.items = [];
        if (add) {
          if (!sl.items.some(function (i) { return String(i.id) === String(everythingItemId); })) {
            sl.items.push({ id: everythingItemId });
          }
        } else {
          sl.items = sl.items.filter(function (i) { return String(i.id) !== String(everythingItemId); });
        }
        cb.disabled = false;
        onChange();
      })
      .catch(function () {
        cb.checked = !add;
        cb.disabled = false;
        showToast(add ? 'Could not add to event list — please try again'
                      : 'Could not remove from event list — please try again');
      });
  }

  // ─── Click handler ───────────────────────────────────────────────────────────

  function onButtonClick(e) {
    e.stopPropagation();
    var btn = e.currentTarget;
    if (btn.dataset.variantId === undefined) return;

    var variantGid = 'gid://shopify/ProductVariant/' + btn.dataset.variantId;

    if (!getSessionToken()) {
      toggleGuest(btn, variantGid);
      return;
    }

    function act() {
      if (variantInEverythingList(variantGid)) {
        removeFromEverythingList(btn, variantGid);
      } else {
        addToEverythingList(btn, variantGid);
      }
    }
    if (wlState.wishlists !== null) {
      act();
    } else {
      if (btn.dataset.wlBusy === '1') return;
      btn.dataset.wlBusy = '1';
      loadAllWishlists().then(function () {
        delete btn.dataset.wlBusy;
        act();
      });
    }
  }

  function addToEverythingList(btn, variantGid) {
    if (btn.dataset.wlBusy === '1') return;
    btn.dataset.wlBusy = '1';
    apiRequest('POST', '/wishlist/items', buildItemPayload(btn))
      .then(function (newItem) {
        wishlisted.add(variantGid);
        var el = getEverythingList();
        if (el) {
          if (!wlState.presenceMap) wlState.presenceMap = new Map();
          if (!wlState.presenceMap.has(variantGid)) wlState.presenceMap.set(variantGid, new Set());
          wlState.presenceMap.get(variantGid).add(el.id);
          if (newItem) {
            if (!el.items) el.items = [];
            if (!el.items.some(function (i) { return String(i.id) === String(newItem.id); })) {
              var savedItem = buildItemPayload(btn);
              savedItem.id = newItem.id;
              if (newItem.added_at) savedItem.added_at = newItem.added_at;
              el.items.push(savedItem);
            }
            return newItem.id;
          }
          return null;
        }
        return loadAllWishlists().then(function () { return getEverythingItemId(variantGid); });
      })
      .then(function (itemId) {
        updateButton(btn);
        updateHeaderCount();
        showToast('Planning for an event? Save to event list', { action: { label: 'Add to event list', onClick: function () {
          if (!itemId) { showToast('Could not open event lists — please try again'); return; }
          openShortlistModal({
            everythingItemId: itemId,
            shortlists: (wlState.wishlists || []).filter(function (w) { return !w.is_default; }),
            onChange: function () { updateButton(btn); updateHeaderCount(); },
          });
        } } });
      })
      .catch(function () { showToast('Could not save — please try again'); })
      .then(function () { delete btn.dataset.wlBusy; }, function () { delete btn.dataset.wlBusy; });
  }

  function removeFromEverythingList(btn, variantGid) {
    if (btn.dataset.wlBusy === '1') return;
    var itemId = getEverythingItemId(variantGid);
    if (!itemId) {
      return loadAllWishlists().then(function () { updateButton(btn); updateHeaderCount(); });
    }
    btn.dataset.wlBusy = '1';

    var memberShortlistIds = (wlState.wishlists || [])
      .filter(function (w) {
        return !w.is_default && (w.items || []).some(function (i) { return String(i.id) === String(itemId); });
      })
      .map(function (w) { return w.id; });
    var snapshotPayload = buildItemPayload(btn);

    if (wlState.presenceMap) wlState.presenceMap.delete(variantGid);
    wishlisted.delete(variantGid);
    (wlState.wishlists || []).forEach(function (w) {
      if (w.items) w.items = w.items.filter(function (i) { return String(i.id) !== String(itemId); });
    });
    updateButton(btn);
    updateHeaderCount();

    apiRequest('DELETE', '/wishlist/items/' + itemId)
      .then(function () {
        var n = memberShortlistIds.length;
        var msg = n ? ('Removed from saved & ' + n + ' event list' + (n !== 1 ? 's' : '')) : 'Removed';
        showToast(msg, { action: { label: 'Undo', onClick: function () {
          undoRemoveFromEverything(btn, variantGid, snapshotPayload, memberShortlistIds);
        } } });
      })
      .catch(function () {
        loadAllWishlists().then(function () { updateButton(btn); updateHeaderCount(); });
        showToast('Could not remove — please try again');
      })
      .then(function () { delete btn.dataset.wlBusy; }, function () { delete btn.dataset.wlBusy; });
  }

  function undoRemoveFromEverything(btn, variantGid, snapshotPayload, shortlistIds) {
    if (btn.dataset.wlBusy === '1') return;
    btn.dataset.wlBusy = '1';
    var wantedIds = shortlistIds || [];
    apiRequest('POST', '/wishlist/items', snapshotPayload)
      .then(function (newItem) {
        return loadAllWishlists().then(function () {
          var freshId = getEverythingItemId(variantGid) || (newItem && newItem.id);
          if (!wantedIds.length) return { failed: 0 };
          if (!freshId) return { failed: wantedIds.length };
          var failed = 0;
          return Promise.all(wantedIds.map(function (id) {
            return apiRequest('POST', '/wishlist/' + id + '/items', { wishlist_item_id: String(freshId) })
              .then(null, function () { failed++; });
          })).then(function () { return { failed: failed }; });
        });
      })
      .then(function (summary) {
        return loadAllWishlists().then(function () {
          updateButton(btn);
          updateHeaderCount();
          if (summary && summary.failed > 0) {
            showToast('Restored to saved, but could not re-add to ' + summary.failed +
                      ' event list' + (summary.failed !== 1 ? 's' : ''));
          } else {
            showToast('Restored');
          }
        });
      })
      .catch(function () { showToast('Could not undo — please try again'); })
      .then(function () { delete btn.dataset.wlBusy; }, function () { delete btn.dataset.wlBusy; });
  }

  function showGuestSaveToast() {
    var i18n = (cfg && cfg.i18n) || {};
    var savedMsg = i18n.guestToastSaved || 'Saved';
    var ctaText = i18n.guestToastCtaText || 'Log in to save, plan and share your event lists';
    var ctaLabel = i18n.guestToastCta || 'Log in';
    var loginUrl = (cfg && cfg.loginUrl) || '/account/login';
    var shown = false;
    try { shown = sessionStorage.getItem('wishlist_guest_cta_shown') === '1'; } catch (e) {}
    if (shown) { showToast(savedMsg); return; }
    try { sessionStorage.setItem('wishlist_guest_cta_shown', '1'); } catch (e) {}
    showToast(ctaText, { action: { label: ctaLabel, onClick: function () {
      window.location.href = loginUrl;
    } } });
  }

  function toggleGuest(btn, variantGid) {
    var items = getGuestItems();
    var idx = items.findIndex(function (i) { return i.variant_id === variantGid; });

    if (idx > -1) {
      items.splice(idx, 1);
      wishlisted.delete(variantGid);
      saveGuestItems(items);
      updateButton(btn);
      updateHeaderCount();
    } else {
      var payload = buildItemPayload(btn);
      payload.added_at = new Date().toISOString();
      items.push(payload);
      wishlisted.add(variantGid);
      saveGuestItems(items);
      updateButton(btn);
      updateHeaderCount();
      showGuestSaveToast();
    }
  }

  // ─── Merge guest items after login ───────────────────────────────────────────

  function mergeGuestWishlist() {
    var items = getGuestItems();
    if (!items.length) return Promise.resolve();

    return apiRequest('POST', '/wishlist/merge', {
      store_domain: cfg.storeDomain,
      items: items.map(function (i) {
        return {
          product_id: i.product_id,
          variant_id: i.variant_id,
          product_handle: i.product_handle,
          product_title: i.product_title,
          image_url: i.image_url,
          price: i.price,
          currency: i.currency || 'NZD',
        };
      }),
    }).then(function () {
      localStorage.removeItem(GUEST_KEY);
    }).catch(function () { /* silent */ });
  }

  // ─── Init ────────────────────────────────────────────────────────────────────

  // Resolves once init()'s login/hydrate chain has settled, in every branch —
  // never rejects. wishlist-button.js loads `defer`, so it runs before
  // DOMContentLoaded but init()'s login is still async; page.wishlist.liquid's
  // DOMContentLoaded handler can otherwise run its loader mid-login, find no
  // token yet, and render the guest list to a logged-in customer.
  var wlReadyResolve;
  var wlReady = new Promise(function (resolve) { wlReadyResolve = resolve; });

  // The auth/hydrate half of init(). Kept separate so init() can guarantee
  // wlReadyResolve() runs even when this throws synchronously — page.wishlist.liquid
  // awaits `ready` before rendering anything, so an unresolved promise there
  // leaves the page as a permanently blank static shell.
  function initAuthAndHydrate() {
    // On a recipient shared view (?shared=TOKEN), renderSharedView() establishes
    // the session itself via ensureRecipientSession(). Skip init's auth so a
    // shared-page load doesn't fire two concurrent /auth/login round-trips.
    var isSharedView = /[?&]shared=/.test(window.location.search);

    if (cfg.customer && isSharedView) {
      // Recipient shared view owns its session; nothing to log in or hydrate here.
      wlReadyResolve();
    } else if (cfg.customer) {
      var storedCustomerId = getStoredCustomerId();
      var isNewLogin = !getSessionToken() || storedCustomerId !== String(cfg.customer.id);

      if (isNewLogin) {
        reauth()
          .then(function () { return mergeGuestWishlist(); })
          .then(function () { return loadWishlistedIds(); })
          .catch(function (err) {
            // Most likely cause in production: the theme is live against a
            // backend that predates the timestamped HMAC, so /auth/login 401s
            // with INVALID_HMAC forever. Without this the customer silently
            // falls through to the guest list and it reads as data loss.
            if (window.console && console.warn) {
              console.warn('Wishlist: login failed, falling back to the guest list —', err);
            }
            return loadWishlistedIds();
          })
          .then(function () { wlReadyResolve(); }, function () { wlReadyResolve(); });
      } else {
        loadWishlistedIds().then(function () { wlReadyResolve(); }, function () { wlReadyResolve(); });
      }
    } else {
      // No Shopify customer — discard any stale wishlist session left from a
      // prior login in this tab. sessionStorage survives a Shopify logout, and a
      // leftover (still-valid) token would otherwise keep a guest on the
      // authenticated API — seeing/writing another account's saves + shortlists.
      // Clearing here is the single source of truth: getSessionToken() then
      // reports guest everywhere (onButtonClick → toggleGuest, wishlist page → guest render).
      try {
        sessionStorage.removeItem(SESSION_TOKEN_KEY);
        sessionStorage.removeItem(SESSION_CUSTOMER_KEY);
      } catch (e) {}
      loadWishlistedIds().then(function () { wlReadyResolve(); }, function () { wlReadyResolve(); });
    }
  }

  function init() {
    try {
      initAuthAndHydrate();
    } catch (e) {
      if (window.console && console.warn) {
        console.warn('Wishlist: init failed, continuing in guest mode —', e);
      }
      wlReadyResolve();
    }

    // Bind buttons already in DOM
    document.querySelectorAll('.wishlist-btn').forEach(function (btn) {
      btn.addEventListener('click', onButtonClick);
    });

    // Observe new buttons added dynamically (e.g. quick-view modals)
    var observer = new MutationObserver(function (mutations) {
      mutations.forEach(function (m) {
        m.addedNodes.forEach(function (node) {
          if (node.nodeType !== 1) return;
          var btns = node.querySelectorAll ? node.querySelectorAll('.wishlist-btn') : [];
          btns.forEach(function (btn) {
            btn.addEventListener('click', onButtonClick);
            updateButton(btn);
          });
        });
      });
    });
    observer.observe(document.body, { childList: true, subtree: true });

    // Keep PDP button in sync when the customer picks a different variant
    document.addEventListener('on:variant:change', function (e) {
      var variant = e.detail && e.detail.variant;
      if (!variant) return;
      var pdpBtn = document.querySelector('.wishlist-btn--pdp');
      if (!pdpBtn) return;
      pdpBtn.dataset.variantId = String(variant.id);
      pdpBtn.dataset.price = variant.price ? (variant.price / 100).toFixed(2) : '';
      updateButton(pdpBtn);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Expose minimal public API for theme developers
  window.Wishlist = {
    reload: loadWishlistedIds,
    isWishlisted: function (variantId) { return variantInAnyWishlist('gid://shopify/ProductVariant/' + variantId); },
    getGuestItems: getGuestItems,
    saveGuestItems: saveGuestItems,
    getWishlists: function () { return wlState.wishlists; },
    openShortlistModal: openShortlistModal,
    toast: showToast,
    // Retry-wrapped API layer — page.wishlist.liquid consumes these instead of
    // keeping its own duplicate copy.
    api: apiRequest,
    // Single definition of "this error means the session is dead" — the wishlist
    // page must not re-derive it inline or the two copies drift apart again.
    isAuthError: isAuthError,
    getSessionToken: getSessionToken,
    ready: wlReady,
    // Sharing (owner)
    shareShortlist: shareShortlist,
    unshareShortlist: unshareShortlist,
    buildShareUrl: buildShareUrl,
    copyToClipboard: copyToClipboard,
    // Shared view (recipient)
    renderSharedView: renderSharedView,
    hasCustomer: function () { return !!cfg.customer; },
  };
})();
